import { Grid, Stack, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { EditableFieldValueView } from './EditableFieldValueView';
import { EditableMarkupField, MarkupChange, SalesPriceEditState } from '../../store';
import { SalesCoverageInput, SalesPriceInput } from './InputFields';

interface Props {
  currentMarkupPrice?: number;
  currentCoverageDegree?: number;
  isActivePrice: boolean;
  includeVat: boolean;
  salesPriceEditState?: SalesPriceEditState;
  newMarkup?: MarkupChange;
  openEditMode?: (field: EditableMarkupField) => void;
  editProps?: {
    isEditingCoverage: boolean;
    isEditingPrice: boolean;
    cancelEditMode: () => void;
    onChangeSalesCoverage: (value: number) => void;
    onChangeSalesPrice: (value: number) => void;
  };
}

export function MarkupAccordionSummary({
  currentMarkupPrice,
  currentCoverageDegree,
  isActivePrice,
  includeVat,
  salesPriceEditState,
  newMarkup,
  openEditMode,
  editProps,
}: Props) {
  const {
    i18n: { language },
  } = useTranslation();
  const t = useMyAssortmentTFunction();

  const newCoverageDegree = newMarkup?.salesCoverage;
  const newMarkupPrice = includeVat ? newMarkup?.salesPriceIncVat : newMarkup?.salesPrice;

  return (
    <Stack component={Grid} container flexDirection="row" alignItems="center" width="100%">
      <Grid size={3.7}>
        <Typography variant="body2" color={!isActivePrice ? 'text.disabled' : 'text.primary'}>
          {t('myAssortment.markup')}
        </Typography>
      </Grid>
      <Typography variant="body2" pr={1} color="text.secondary">
        {t('myAssortment.columns.salesCoverage')}:
      </Typography>
      <Grid size={4}>
        {editProps?.isEditingCoverage ? (
          <SalesCoverageInput
            defaultValue={newCoverageDegree ?? currentCoverageDegree ?? 0}
            onSubmit={editProps.onChangeSalesCoverage}
            onCancel={editProps.cancelEditMode}
          />
        ) : (
          <EditableFieldValueView
            currentValue={
              currentCoverageDegree
                ? `${currentCoverageDegree.toLocaleString(language, { maximumFractionDigits: 2 })}%`
                : '-'
            }
            newValue={
              newCoverageDegree
                ? `${newCoverageDegree.toLocaleString(language, { maximumFractionDigits: 2 })}%`
                : undefined
            }
            isApproximate={newMarkup?.editedField !== 'coverage'}
            openEditMode={openEditMode ? () => openEditMode('coverage') : undefined}
            disabled={salesPriceEditState === 'noCalculationForPackage'}
          />
        )}
      </Grid>
      <Grid>
        {editProps?.isEditingPrice ? (
          <SalesPriceInput
            defaultValue={newMarkupPrice ?? currentMarkupPrice ?? 0}
            onSubmit={editProps.onChangeSalesPrice}
            onCancel={editProps.cancelEditMode}
          />
        ) : (
          <EditableFieldValueView
            currentValue={
              currentMarkupPrice
                ? currentMarkupPrice.toLocaleString(language, {
                    maximumFractionDigits: 2,
                  })
                : '-'
            }
            newValue={
              newMarkupPrice
                ? t('myAssortment.productCard.price', {
                    price: newMarkupPrice.toLocaleString(language, {
                      maximumFractionDigits: 2,
                    }),
                  })
                : undefined
            }
            isApproximate={newMarkup?.editedField !== 'price'}
            openEditMode={openEditMode ? () => openEditMode('price') : undefined}
            disabled={salesPriceEditState !== 'editable'}
            disabledExplanation={
              salesPriceEditState !== 'editable' && salesPriceEditState
                ? t(`myAssortment.productCard.salesPrice.error.${salesPriceEditState}`)
                : undefined
            }
          />
        )}
      </Grid>
    </Stack>
  );
}
